// DispatchConfirm.jsx
import React, { useEffect, useState, useRef } from "react";
import request from "../../utils/request";
import PageContainer from "../../components/PageContainer/PageContainer";
import {
  FiCheckCircle,
  FiXCircle,
  FiAlertTriangle,
  FiSearch,
  FiCalendar,
  FiUser,
} from "react-icons/fi";
import { Tooltip } from "@material-ui/core";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import styles from "./DispatchConfirm.module.css";
import df from "../../utils/dateFormater";
import t from "../../utils/translation";

export default function DispatchConfirm() {
  const [dispatchList, setDispatchList] = useState([]);
  const [dispatchNumber, setDispatchNumber] = useState("");
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [dispatch, setDispatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const dropdownRef = useRef();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchDispatchNumbers = async () => {
    try {
      const { data } = await request().get(
        `/api/parameter/dispatch?dispatchStatus=1`
      );
      const valid = Array.isArray(data)
        ? data.filter((n) => typeof n === "number")
        : [];
      setDispatchList(valid);
      setFilteredSuggestions(valid);
    } catch (err) {
      console.error("Error loading dispatch numbers", err);
    }
  };
  useEffect(() => {
    fetchDispatchNumbers();
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setDispatchNumber(val);
    const filtered =
      val === ""
        ? dispatchList
        : dispatchList.filter((num) => num?.toString().includes(val));
    setFilteredSuggestions(filtered);
    setShowDropdown(true);
  };

  const handleInputFocus = () => {
    const filtered =
      dispatchNumber === ""
        ? dispatchList
        : dispatchList.filter((num) =>
            num?.toString().includes(dispatchNumber)
          );
    setFilteredSuggestions(filtered);
    setShowDropdown(true);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter") {
      if (filteredSuggestions.length === 1) {
        selectSuggestion(filteredSuggestions[0]);
      } else {
        fetchDispatch(dispatchNumber);
        setShowDropdown(false);
      }
    }
  };

  const selectSuggestion = (num) => {
    const asString = num.toString();
    setDispatchNumber(asString);
    setShowDropdown(false);
    fetchDispatch(asString);
  };

  const fetchDispatch = async (num) => {
    const parsedNum = Number(num);
    if (!dispatchList.includes(parsedNum)) {
      toast.info(t("BlankEntry208"));
      return;
    }

    try {
      setLoading(true);
      setDispatch(null);
      const { data } = await request().get(
        `/api/dispatch/list?dispatchNumber=${parsedNum}`
      );
      const found = data.dispatches?.[0] || data[0];
      if (found?.packages) {
        found.packages = found.packages.map((p) => ({
          ...p,
          packageStatus: null,
        }));
      }
      setDispatch(found || null);
    } catch (err) {
      console.error("Error fetching dispatch:", err);
      setDispatch(null);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (pkgId, status) => {
    setDispatch((prev) => ({
      ...prev,
      packages: prev.packages.map((p) =>
        p.packageId === pkgId ? { ...p, packageStatus: status } : p
      ),
    }));
  };

  const confirmStatuses = async () => {
    if (!dispatch?.dispatchNumber) return;

    const allValid = dispatch.packages.every((p) =>
      [8, 9, 11].includes(p.packageStatus)
    );
    if (!allValid) {
      toast.info(t("BlankEntry209"));
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        dispatchNumber: dispatch.dispatchNumber,
        dispatchObservation: dispatch.dispatchObservation || "",
        packages: dispatch.packages.map((p) => ({
          packageNumber: p.packageNumber,
          packageStatus: p.packageStatus,
          observations: p.dispatchObservation || "",
        })),
      };

      await request().put(`/api/dispatch/receive`, payload);
      fetchDispatchNumbers();
      toast.success(t("BlankEntry210"));
      setDispatch(null);
      setDispatchNumber("");
    } catch (err) {
      console.error("Error confirming :", err);
      toast.error("An error occurred while confirming.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer
      title={t("BlankEntry169") || "Dispatch Confirm"}
      category={t("BlankEntry166") || "Dispatch"}
      categoryId={4}
      backUrl="/appdispatchmenu"
    >
      <div className={styles.screenWrapper}>
        <div className={styles.searchContainer} ref={dropdownRef}>
          <FiSearch />
          <input
            type="text"
            placeholder={t("BlankEntry201") || "Dispatch number"}
            value={dispatchNumber}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onFocus={handleInputFocus}
          />
          <button onClick={() => fetchDispatch(dispatchNumber)}>
            {t("BlankEntry117")}
          </button>
          {showDropdown && (
            <ul className={styles.suggestions}>
              {filteredSuggestions.length > 0 ? (
                filteredSuggestions.map((num) => (
                  <li key={num} onClick={() => selectSuggestion(num)}>
                    {num}
                  </li>
                ))
              ) : (
                <li className={styles.noSuggestion}>{t("BlankEntry211")}</li>
              )}
            </ul>
          )}
        </div>

        {loading && (
          <div
            style={{
              marginTop: "2rem",
              display: "flex",
              alignItems: "center",
              width: "100%",
              gap: "5px",
            }}
          >
            <p
              style={{
                marginBottom: "0px",
              }}
            >
              <div className={styles.spinner} />
              {t("BlankEntry115")}...
            </p>
          </div>
        )}

        {dispatch && (
          <motion.div
            className={styles.dispatchDetails}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <p className={styles.dispatchTitle}>
              {t("BlankEntry166") || "Dispatch"} {dispatch.dispatchNumber}
            </p>

            <div className={styles.dispatchInfo}>
              <div className={styles.infoItem}>
                <FiUser className={styles.infoIcon} />
                <span>
                  <strong>{t("BlankEntry206")}:</strong> {dispatch.senderUser}
                </span>
              </div>
              <div className={styles.infoItem}>
                <FiAlertTriangle className={styles.infoIcon} />
                <span>
                  <strong>{t("BlankEntry173")}:</strong>{" "}
                  {dispatch.shippingResponsible}
                </span>
              </div>
              <div className={styles.infoItem}>
                <FiSearch className={styles.infoIcon} />
                <span>
                  <strong>{t("BlankEntry207")}:</strong> {dispatch.areaName}
                </span>
              </div>
              <div
                className={`${styles.infoItem} ${
                  dispatch.isUrgent ? styles.urgent : styles.scheduled
                }`}
              >
                {dispatch.isUrgent ? (
                  <FiAlertTriangle className={styles.infoIcon} />
                ) : (
                  <FiCalendar className={styles.infoIcon} />
                )}
                <span>
                  <strong>{t("BlankEntry174")}:</strong>{" "}
                  {dispatch.isUrgent ? t("BlankEntry176") : t("BlankEntry177")}
                </span>
              </div>
              <div className={styles.infoItem}>
                <FiCalendar className={styles.infoIcon} />
                <span>
                  <strong>{t("DateSpan")}:</strong>{" "}
                  {df(dispatch.creationDate, "dateTime")}
                </span>
              </div>

              {dispatch.observations && (
                <div className={styles.observationBox}>
                  <strong>{t("ObservationSpan")}:</strong>{" "}
                  <em>{dispatch.observations}</em>
                </div>
              )}
              <div className={styles.packageObservationEdit}>
                <label className={styles.packageObservationEditLabel}>
                  {`${t("ObservationSpan")}:`}
                </label>
                <textarea
                  rows={2}
                  placeholder={`${t("BlankEntry213")}...`}
                  value={dispatch.dispatchObservation || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setDispatch((prev) => ({
                      ...prev,
                      dispatchObservation: val,
                    }));
                  }}
                />
              </div>
            </div>

            {dispatch.packages.map((pkg) => (
              <motion.div
                key={pkg.packageId}
                className={styles.packageCard}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className={styles.packageHeader}>
                  <div className={styles.packageTitle}>
                    <div>
                      <strong>{pkg.packageNumber}</strong> — {pkg.packageName}
                    </div>
                    <div className={styles.packageMeta}>
                      <span className={styles.materialCountBadge}>
                        {pkg.materials.length}{" "}
                        {pkg.materials.length === 1
                          ? t("MaterialSpan")
                          : t("MaterialsSpan")}
                      </span>
                      {pkg.observations && (
                        <div className={styles.packageObservation}>
                          <strong>{t("ObservationSpan")}:</strong>{" "}
                          <em>{pkg.observations}</em>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={styles.statusButtons}>
                    <Tooltip title="Accepted in Area" placement="top">
                      <button
                        className={`${styles.statusBtn} ${
                          pkg.packageStatus === 8 ? styles.accepted : ""
                        }`}
                        onClick={() => handleStatusChange(pkg.packageId, 8)}
                      >
                        <FiCheckCircle /> {t("BlankEntry214")}
                      </button>
                    </Tooltip>
                    <Tooltip title="Rejected in Area" placement="top">
                      <button
                        className={`${styles.statusBtn} ${
                          pkg.packageStatus === 9 ? styles.rejected : ""
                        }`}
                        onClick={() => handleStatusChange(pkg.packageId, 9)}
                      >
                        <FiXCircle /> {t("BlankEntry144")}
                      </button>
                    </Tooltip>
                    <Tooltip title="Package is missing" placement="top">
                      <button
                        className={`${styles.statusBtn} ${
                          pkg.packageStatus === 11 ? styles.missing : ""
                        }`}
                        onClick={() => handleStatusChange(pkg.packageId, 11)}
                      >
                        <FiAlertTriangle /> {t("BlankEntry200")}
                      </button>
                    </Tooltip>
                  </div>
                </div>
                <div className={styles.packageObservationEdit}>
                  <label className={styles.packageObservationEditLabel}>
                    {t("ObservationSpan")}:
                  </label>
                  <textarea
                    rows={2}
                    placeholder={t("ObservationSpan")}
                    value={pkg.dispatchObservation || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      setDispatch((prev) => ({
                        ...prev,
                        packages: prev.packages.map((p) =>
                          p.packageId === pkg.packageId
                            ? { ...p, dispatchObservation: val }
                            : p
                        ),
                      }));
                    }}
                  />
                </div>
              </motion.div>
            ))}

            <div className={styles.footerConfirmWrapper}>
              <button
                className={styles.confirmBtn}
                onClick={confirmStatuses}
                disabled={submitting}
              >
                {submitting ? `${t("BlankEntry216")}...` : t("ConfirmButton")}
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </PageContainer>
  );
}
